import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, UserIcon } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { useDocumentAccess } from '../../hooks/useDocumentAccess';
import { searchStudents } from '../../services/caseNotes';

type Result = Awaited<ReturnType<typeof searchStudents>>[number];

const MIN_QUERY = 2;
const LIMIT = 8;

/**
 * ⌘K / Ctrl+K student search for staff (#363).
 *
 * Reuses searchStudents (name ilike on learning_diversity.students, backed by
 * idx_students_name_trgm), so RLS decides which students come back. Only mounted
 * for signed-in staff. LD Assistants, who can't open Documents, go to the student's
 * case notes instead of the student profile.
 */
export const CommandPalette: React.FC = () => {
  const { user } = useAuth();
  const isStaff = user?.role === 'staff';
  if (!isStaff) return null;
  return <StaffCommandPalette />;
};

const StaffCommandPalette: React.FC = () => {
  const navigate = useNavigate();
  const { canAccessDocuments } = useDocumentAccess();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Result[]>([]);
  const [active, setActive] = useState(0);
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const inputRef = useRef<HTMLInputElement>(null);
  const restoreFocus = useRef<HTMLElement | null>(null);
  const listId = useId();

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    setResults([]);
    setActive(0);
    restoreFocus.current?.focus?.();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(prev => {
          if (!prev) restoreFocus.current = document.activeElement as HTMLElement | null;
          return !prev;
        });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    const term = query.trim();
    if (!open || term.length < MIN_QUERY) {
      setResults([]);
      setStatus('idle');
      return;
    }
    const controller = new AbortController();
    setStatus('loading');
    const timer = window.setTimeout(() => {
      searchStudents(term, LIMIT, controller.signal)
        .then(rows => {
          setResults(rows);
          setActive(0);
          setStatus('idle');
        })
        .catch(() => {
          if (!controller.signal.aborted) setStatus('error');
        });
    }, 200);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [query, open]);

  const go = (student: Result) => {
    navigate(canAccessDocuments ? `/staff/students/${student.id}` : `/staff/case-notes/student/${student.id}`);
    close();
  };

  const onInputKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && results[active]) {
      e.preventDefault();
      go(results[active]);
    }
  };

  if (!open) return null;

  const term = query.trim();
  const optionId = (i: number) => `${listId}-opt-${i}`;

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/40 px-4 pt-[12vh]" onMouseDown={close}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Find a student"
        className="w-full max-w-lg overflow-hidden rounded-[16px] bg-white shadow-card-md"
        onMouseDown={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-surface-sunken px-4">
          <SearchIcon className="h-5 w-5 shrink-0 text-ink-secondary" aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded={results.length > 0}
            aria-controls={listId}
            aria-activedescendant={results[active] ? optionId(active) : undefined}
            aria-autocomplete="list"
            aria-label="Search students by name"
            placeholder="Search students by name…"
            className="h-14 w-full bg-transparent text-base text-ink-primary placeholder:text-ink-secondary focus:outline-none"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={onInputKey}
          />
          <kbd className="hidden rounded border border-surface-sunken px-1.5 py-0.5 text-xs text-ink-secondary sm:inline">Esc</kbd>
        </div>
        <ul id={listId} role="listbox" aria-label="Students" className="max-h-80 overflow-y-auto py-2">
          {results.map((s, i) => (
            <li
              key={s.id}
              id={optionId(i)}
              role="option"
              aria-selected={i === active}
              className={`mx-2 flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 ${i === active ? 'bg-surface-muted' : ''}`}
              onMouseEnter={() => setActive(i)}
              onClick={() => go(s)}
            >
              <UserIcon className="h-4 w-4 shrink-0 text-ink-secondary" aria-hidden="true" />
              <span className="flex-1 truncate text-sm font-medium text-ink-primary">{s.name}</span>
              {s.yearLevel && <span className="text-xs text-ink-secondary">Year {s.yearLevel}</span>}
            </li>
          ))}
        </ul>
        <p className="border-t border-surface-sunken px-4 py-2 text-xs text-ink-secondary" aria-live="polite">
          {term.length < MIN_QUERY
            ? 'Type at least two letters. ↑ ↓ to move, Enter to open.'
            : status === 'loading'
              ? 'Searching…'
              : status === 'error'
                ? "Search didn't work. Try again."
                : results.length === 0
                  ? 'No students match.'
                  : `${results.length} student${results.length === 1 ? '' : 's'} found.`}
        </p>
      </div>
    </div>
  );
};

export default CommandPalette;
